// src/components/RealIdleTimeWidget.tsx - MOBILE RESPONSIVE
import React from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, CartesianGrid } from 'recharts';

interface Props {
  data: any[];
}

const RealIdleTimeWidget: React.FC<Props> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!data || data.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography color="textSecondary" variant="body2" textAlign="center">
          No idle time data available
        </Typography>
      </Box>
    );
  }

  const totalIdleMinutes = data.reduce((sum, item) => sum + (item.totalIdleMinutes || 0), 0);
  const totalIdleHours = Math.round(totalIdleMinutes / 60);
  const avgIdleHours = totalIdleMinutes > 0 ? Math.round(totalIdleMinutes / 60 / data.length * 10) / 10 : 0;

  const chartData = data
    .map(item => ({
      vehicle: item.device?.plateNumber || item.imei?.slice(-6) || 'Unknown',
      idleHours: Math.round(item.totalIdleMinutes / 60 * 10) / 10,
      idleEvents: item.idleEvents
    }))
    .sort((a, b) => b.idleHours - a.idleHours)
    .slice(0, isMobile ? 3 : 5);

  const maxIdle = Math.max(...chartData.map(d => d.idleHours), 1);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: isMobile ? 0.5 : 1 }}>
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant={isMobile ? 'h4' : 'h3'} 
          sx={{ fontWeight: 'bold', color: '#ff9800' }}
        >
          {totalIdleHours} hrs
        </Typography>
        <Typography 
          variant="body2" 
          color="textSecondary"
          sx={{ fontSize: isMobile ? 11 : 13 }}
        >
          Total idle time • Avg {avgIdleHours} hrs/vehicle
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 150 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData} 
            layout="horizontal"
            margin={{ 
              top: 5, 
              right: isMobile ? 5 : 20, 
              left: isMobile ? 5 : 20, 
              bottom: 5 
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis 
              dataKey="vehicle"
              type="category"
              axisLine={false} 
              tickLine={false}
              tick={{ fontSize: isMobile ? 9 : 11 }}
              angle={isMobile ? -45 : 0}
              textAnchor={isMobile ? 'end' : 'middle'}
              height={isMobile ? 60 : 30}
            />
            <YAxis 
              type="number"
              domain={[0, maxIdle * 1.1]}
              axisLine={false} 
              tickLine={false}
              tick={{ fontSize: isMobile ? 10 : 11 }}
              tickFormatter={(value) => `${value}h`}
              width={isMobile ? 35 : 45}
            />
            <Tooltip 
              formatter={(value: number, name: string, props: any) => [
                `${value} hours (${props.payload.idleEvents} events)`,
                'Idle Time'
              ]}
              contentStyle={{ 
                borderRadius: 8, 
                border: '1px solid #e0e0e0',
                fontSize: isMobile ? 11 : 13 
              }}
            />
            <Bar dataKey="idleHours" radius={[8, 8, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.idleHours > avgIdleHours * 1.5 ? '#f44336' : '#ff9800'} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {chartData.length > 0 && (
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            display="block"
            sx={{ fontSize: isMobile ? 10 : 12 }}
          >
            Highest: {chartData[0]?.vehicle} ({chartData[0]?.idleHours}h)
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RealIdleTimeWidget;